import { portfolioProjects, portfolioCategories } from '../../../mocks/portfolio';
import { useState, useEffect, useRef, useCallback } from 'react';

export default function Portfolio() {
  const [isVisible, setIsVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<typeof portfolioProjects[0] | null>(null);
  const [activeFilter, setActiveFilter] = useState('All');
  const sectionRef = useRef<HTMLDivElement>(null);

  const filteredProjects = activeFilter === 'All'
    ? portfolioProjects
    : portfolioProjects.filter((p) => p.category === activeFilter);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [modalOpen]);

  const handleCardClick = useCallback((project: typeof portfolioProjects[0]) => {
    setSelectedProject(project);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setTimeout(() => setSelectedProject(null), 300);
  }, []);

  return (
    <>
      <section id="portfolio" className="py-12 sm:py-20 bg-[#FAFAF8] font-body" ref={sectionRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-[1px] bg-[#F5C518]"></div>
              <span className="text-[#F5C518] text-xs font-semibold tracking-[0.3em] uppercase">Portfolio</span>
              <div className="w-8 h-[1px] bg-[#F5C518]"></div>
            </div>

            {/* Category filter tabs */}
            <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
              {portfolioCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveFilter(cat)}
                  className={`px-5 py-2 rounded-full text-xs font-semibold tracking-wider uppercase transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeFilter === cat
                      ? 'bg-[#1a1a1a] text-white shadow-md'
                      : 'bg-white text-[#777] border border-[#e0e0e0] hover:border-[#1a1a1a] hover:text-[#1a1a1a]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div
            className={`grid grid-cols-1 ${filteredProjects.length === 1 ? 'lg:grid-cols-1 max-w-2xl mx-auto' : 'lg:grid-cols-2'} gap-6 sm:gap-8 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {filteredProjects.map((project, index) => (
              <div
                key={project.name}
                className="group flex flex-col"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div
                  onClick={() => handleCardClick(project)}
                  className="block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 cursor-pointer border border-[#eee] flex-1"
                >
                  {/* Browser mockup frame */}
                  <div className="bg-[#1a1a1a] px-4 py-3 flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]"></div>
                    </div>
                    <div className="flex-1 flex justify-center">
                      <div className="bg-[#2a2a2a] rounded-md px-4 py-1 text-[#999] text-xs flex items-center gap-2 max-w-xs w-full justify-center">
                        <i className="ri-lock-line text-[10px]"></i>
                        <span className="truncate">{project.url.replace('https://', '')}</span>
                      </div>
                    </div>
                    <div className="w-[52px]"></div>
                  </div>

                  {/* Preview area */}
                  <div className="relative w-full h-[280px] sm:h-[320px] flex flex-col items-center justify-center bg-[#f5f5f3]">
                    <span className="text-[10px] font-semibold tracking-widest uppercase text-[#F5C518] bg-[#F5C518]/10 px-3 py-1 rounded-full mb-4">{project.category}</span>
                    <h3 className="text-2xl sm:text-3xl text-[#1a1a1a] font-display tracking-wide mb-6">{project.name}</h3>
                    <div className="flex items-center gap-2 bg-[#1a1a1a] text-white px-6 py-3 rounded-full group-hover:bg-[#F5C518] group-hover:text-[#1a1a1a] transition-all duration-300 shadow-md whitespace-nowrap">
                      <i className="ri-eye-line text-base"></i>
                      <span className="text-xs font-semibold tracking-wider uppercase">Preview Site</span>
                    </div>
                  </div>
                </div>

                {/* Project info below */}
                <div className="mt-6 flex flex-col gap-3">
                  <div>
                    <p className="text-[#777] leading-relaxed text-[13px] line-clamp-2">{project.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal with live site iframe */}
      {modalOpen && selectedProject && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          onClick={handleCloseModal}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fadeIn"></div>

          {/* Modal content */}
          <div
            className="relative w-[95vw] h-[90vh] max-w-[1400px] rounded-xl overflow-hidden shadow-2xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Browser chrome */}
            <div className="bg-[#1a1a1a] px-4 py-3 flex items-center gap-2">
              <div className="flex gap-1.5">
                <button
                  onClick={handleCloseModal}
                  className="w-3 h-3 rounded-full bg-[#ff5f57] hover:bg-[#ff3b30] transition-colors cursor-pointer flex items-center justify-center group"
                  aria-label="Close"
                >
                  <i className="ri-close-line text-[8px] text-transparent group-hover:text-white"></i>
                </button>
                <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
                <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
              </div>
              <div className="flex-1 flex justify-center">
                <div className="bg-[#2a2a2a] rounded-md px-4 py-1.5 text-[#999] text-xs flex items-center gap-2 max-w-sm w-full justify-center">
                  <i className="ri-lock-line text-[10px]"></i>
                  <span>{selectedProject.url.replace('https://', '')}</span>
                </div>
              </div>
              <a
                href={selectedProject.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#999] hover:text-white transition-colors cursor-pointer w-5 h-5 flex items-center justify-center"
                title="Open in new tab"
              >
                <i className="ri-external-link-line text-sm"></i>
              </a>
            </div>

            {/* Iframe */}
            <div className="w-full bg-white" style={{ height: 'calc(100% - 44px)' }}>
              <iframe
                src={selectedProject.url}
                title={selectedProject.name}
                className="w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
              />
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.25s ease-out forwards;
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
}
